import React, { useState } from 'react';
import CalculatorCard from '../ui/CalculatorCard';
import InputField from '../ui/InputField';
import ResultDisplay from '../ui/ResultDisplay';
import { CalendarHeart, AlertTriangle } from 'lucide-react';
import Accordion from '../ui/Accordion';

// Tarih yardımcı fonksiyonu (gün eklemek için)
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};



// Tarihi DD.MM.YYYY formatına çevir (gösterim için)
const formatDisplayDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Aylar 0'dan başlar
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};


const YumurtlamaDonemiCalculator: React.FC = () => {
  const [lastPeriodDate, setLastPeriodDate] = useState<string>('');
  const [cycleLength, setCycleLength] = useState<string>('28');
  const [nextPeriodDate, setNextPeriodDate] = useState<string | null>(null);
  const [ovulationDate, setOvulationDate] = useState<string | null>(null);
  const [fertileWindowStart, setFertileWindowStart] = useState<string | null>(null);
  const [fertileWindowEnd, setFertileWindowEnd] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  const calculateOvulation = () => {
    if (!lastPeriodDate) {
      setError('Lütfen son adet tarihinizi giriniz.');
      setOvulationDate(null); setFertileWindowStart(null); setFertileWindowEnd(null); setNextPeriodDate(null);
      return;
    }
    const numCycleLength = parseInt(cycleLength);
    if (isNaN(numCycleLength) || numCycleLength < 20 || numCycleLength > 45) {
      setError('Lütfen geçerli bir döngü süresi giriniz (20-45 gün).');
      setOvulationDate(null); setFertileWindowStart(null); setFertileWindowEnd(null); setNextPeriodDate(null);
      return;
    }

    const lmp = new Date(lastPeriodDate);
    if (isNaN(lmp.getTime())) {
      setError('Geçersiz son adet tarihi formatı.');
      setOvulationDate(null); setFertileWindowStart(null); setFertileWindowEnd(null); setNextPeriodDate(null);
      return;
    }

    setError('');

    const nextPeriod = addDays(lmp, numCycleLength);
    setNextPeriodDate(formatDisplayDate(nextPeriod));

    const estimatedOvulation = addDays(nextPeriod, -14);
    setOvulationDate(formatDisplayDate(estimatedOvulation));

    const fertileStart = addDays(estimatedOvulation, -5);
    const fertileEnd = addDays(estimatedOvulation, 1);
    setFertileWindowStart(formatDisplayDate(fertileStart));
    setFertileWindowEnd(formatDisplayDate(fertileEnd));
  };

  return (
    <CalculatorCard title="Yumurtlama Dönemi Hesaplama">
      <div className="flex items-center mb-6">
        <CalendarHeart size={18} className="text-pink-400 mr-2" />
        <h2 className="text-cyan-200">Yumurtlama ve Doğurganlık Takvimi</h2>
      </div>

      <div className="space-y-5">
        <InputField
          id="lastPeriodDate"
          label="Son Adet Tarihinizin İlk Günü"
          value={lastPeriodDate}
          onChange={(e) => setLastPeriodDate(e.target.value)}
          type="date"
        />
        <InputField
          id="cycleLength"
          label="Ortalama Adet Döngüsü Süreniz (gün)"
          value={cycleLength}
          onChange={(e) => setCycleLength(e.target.value)}
          type="number"
          min={20}
          max={45}
          placeholder="Örn. 28"
        />

        {error && (
          <div className="bg-red-900/20 border border-red-700/30 text-red-300 p-3 rounded-lg flex items-center gap-2">
            <AlertTriangle size={20} />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={calculateOvulation}
          className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600
                     text-white font-medium py-3 rounded-md mb-4 transition-all duration-300
                     shadow-md shadow-pink-500/20 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500/50
                     flex items-center justify-center gap-2"
        >
          Hesapla
        </button>

        {ovulationDate && (
          <div className="bg-[#131d2f]/50 border border-cyan-800/30 rounded-lg p-4 space-y-4">
            <ResultDisplay
              label="Tahmini Bir Sonraki Adet Tarihi"
              value={nextPeriodDate || '-'}
              unit=""
            />
            <div className="bg-pink-700/20 p-3 rounded-lg text-center">
              <p className="text-sm text-pink-300 mb-1">Tahmini Yumurtlama Tarihiniz</p>
              <p className="text-xl font-bold text-pink-200">{ovulationDate}</p>
            </div>
            <div className="bg-rose-700/20 p-3 rounded-lg text-center">
              <p className="text-sm text-rose-300 mb-1">Tahmini Doğurganlık Pencereniz</p>
              <p className="text-lg font-semibold text-rose-200">
                {fertileWindowStart} &ndash; {fertileWindowEnd}
              </p>
            </div>
          </div>
        )}

        <Accordion title="Yumurtlama (Ovulasyon) ve Doğurganlık Rehberi">
          <div className="space-y-4 text-gray-300/90">
            <div className="flex items-start text-orange-300 bg-orange-900/20 border border-orange-700/30 rounded-lg p-3">
              <AlertTriangle size={24} className="mr-2 shrink-0" />
              <div>
                <h3 className="font-semibold">Önemli Bilgilendirme</h3>
                <p className="text-gray-400 mt-1">Bu hesaplayıcı, takvim metoduna dayalı bir tahmindir ve tıbbi kesinlik taşımaz. Yumurtlama zamanı kişiden kişiye ve döngüden döngüye değişiklik gösterebilir. Bu araç, doğum kontrol yöntemi olarak <strong className="text-orange-200">kullanılmamalıdır.</strong></p>
              </div>
            </div>

            <p>
              Yumurtlama (ovulasyon), bir kadının adet döngüsünün en önemli evrelerinden biridir. Bu süreçte, olgunlaşan bir yumurta hücresi yumurtalıktan salınır ve döllenme için hazır hale gelir. Yumurtlamanın ne zaman gerçekleştiğini bilmek, gebelik planlayan çiftler için en doğru zamanı belirlemede kritik bir rol oynar. Bu hesaplama aracı, adet döngüsü verilerinize dayanarak bu doğurganlık penceresini tahmin etmenize yardımcı olmak için tasarlanmıştır.
            </p>

            <h3 className="text-lg font-semibold text-gray-100 pt-2">Yumurtlama Hesaplama Aracı Nasıl Kullanılır?</h3>
            <p>Bu araç, "takvim metodu" olarak bilinen bir yöntemle çalışır. Doğru bir tahmin için:</p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li><strong>Son Adet Tarihi:</strong> En son yaşadığınız adet kanamasının <strong>ilk gününü</strong> takvimden seçin.</li>
              <li><strong>Ortalama Döngü Süresi:</strong> Bir adet kanamasının ilk gününden, bir sonraki adet kanamasının ilk gününe kadar geçen ortalama gün sayısını girin. Bu süre genellikle 21 ile 35 gün arasında değişir, en yaygın olanı 28 gündür.</li>
              <li><strong>Hesapla:</strong> Butona tıkladığınızda araç, tahmini yumurtlama gününüzü ve en doğurgan olduğunuz gün aralığını ("doğurganlık penceresi") sizin için hesaplayacaktır.</li>
            </ol>

            <h3 className="text-lg font-semibold text-gray-100 pt-2">Hesaplamanın Arkasındaki Mantık Nedir?</h3>
            <p>Hesaplama, düzenli bir adet döngüsünün temel fazlarına dayanır:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li><strong>Luteal Faz:</strong> Yumurtlama sonrası ile bir sonraki adet dönemi arasındaki bu faz, çoğu kadında genellikle sabit olup yaklaşık 14 gün sürer.</li>
              <li><strong>Tahmin Yöntemi:</strong> Aracımız, bir sonraki adetinizin başlayacağı tahmini tarihi bulmak için son adet tarihinize döngü sürenizi ekler. Ardından, bu gelecek tarihten 14 gün geriye giderek en olası yumurtlama gününü tahmin eder.</li>
              <li><strong>Doğurganlık Penceresi:</strong> Sperm hücreleri kadın vücudunda 5 güne kadar canlı kalabilirken, yumurta hücresinin ömrü yaklaşık 12-24 saattir. Bu nedenle, en yüksek gebelik şansının olduğu "doğurganlık penceresi", tahmini yumurtlama gününden önceki 5 günü ve yumurtlama gününün kendisini kapsayan 6 günlük bir aralık olarak hesaplanır.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-100 pt-2">Doğurganlık Belirtileri ve Diğer Yöntemler</h3>
            <p>Takvim metoduna ek olarak, vücudunuzun verdiği diğer ipuçlarını takip etmek de yumurtlama zamanını belirlemenize yardımcı olabilir:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong>Servikal Mukus Değişikliği:</strong> Yumurtlama yaklaştıkça, servikal sıvı (akıntı) daha berrak, kaygan ve yumurta akına benzer bir kıvama gelir.</li>
              <li><strong>Bazal Vücut Isısı (BBT) Artışı:</strong> Yumurtlamadan hemen sonra vücut ısısında hafif (yaklaşık 0.5°C) ama kalıcı bir artış gözlemlenir. Her sabah uyanır uyanmaz ateş ölçmek, yumurtlamanın gerçekleşip gerçekleşmediğini geriye dönük olarak teyit edebilir.</li>
              <li><strong>Ovulasyon Test Kitleri:</strong> Eczanelerde satılan bu testler, idrardaki Luteinizan Hormon (LH) artışını tespit ederek yumurtlamanın gerçekleşmek üzere olduğunu (genellikle 24-36 saat içinde) haber verir.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-100 pt-2">Sıkça Sorulan Sorular</h3>
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-gray-200">Soru: Bu hesaplayıcı ne kadar güvenilirdir?</p>
                <p><strong>Cevap:</strong> Bu araç, özellikle düzenli adet döngüsüne sahip olanlar için iyi bir <strong>tahmin</strong> sunar. Ancak stres, hastalık, seyahat gibi faktörler döngü düzenini etkileyebilir. Bu nedenle, bu takvim bir kılavuz olarak görülmeli, kesin bir bilimsel sonuç olarak kabul edilmemelidir.</p>
              </div>
              <div>
                <p className="font-semibold text-gray-200">Soru: Adet döngüm düzensizse bu aracı kullanabilir miyim?</p>
                <p><strong>Cevap:</strong> Döngüleriniz çok düzensizse (örneğin bir ay 25, diğer ay 35 gün sürüyorsa), takvim metodunun doğruluğu önemli ölçüde azalır. Bu durumda, son birkaç aydaki en kısa ve en uzun döngülerinizi not alarak veya ovulasyon test kitleri gibi diğer yöntemleri kullanarak daha güvenilir sonuçlar elde edebilirsiniz.</p>
              </div>
              <div>
                <p className="font-semibold text-gray-200">Soru: Bu hesap makinesi doğum kontrolü için kullanılabilir mi?</p>
                <p><strong>Cevap:</strong> <strong>KESİNLİKLE HAYIR.</strong> Bu araç, gebelikten korunma yöntemi olarak tasarlanmamıştır ve bu amaçla kullanılmamalıdır. Doğurganlık penceresi sadece bir tahmindir ve yumurtlama zamanı beklenmedik şekilde değişebilir. Güvenilir doğum kontrol yöntemleri için lütfen bir sağlık uzmanına danışın.</p>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-100 pt-2">İlgili Hesaplama Araçlarımız</h3>
            <ul className="list-disc list-inside pl-2">
              <li><a href="/yas-hesaplama" className="text-cyan-400 hover:underline">Yaş Hesaplama</a></li>
              <li><a href="/kac-gun-oldu-hesaplama" className="text-cyan-400 hover:underline">Kaç Gün Oldu Hesaplama</a></li>
              <li><a href="/vucut-kitle-indeksi" className="text-cyan-400 hover:underline">Vücut Kitle İndeksi</a></li>
              <li><a href="/gunluk-su-ihtiyaci" className="text-cyan-400 hover:underline">Günlük Su İhtiyacı</a></li>
            </ul>
          </div>
        </Accordion>
      </div>
    </CalculatorCard>
  );
};

export default YumurtlamaDonemiCalculator;